import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import FaqGroupsSection, { toFaqGroup } from "@/components/FaqGroupsSection";
import { itemListSchema, collectionPageSchema, breadcrumbSchema } from "@/lib/seo-schemas";

type ProductItem = {
  name: string;
  slug: string;
  image: string;
  external?: string;
  ctaKey?: string;
};

const products: ProductItem[] = [
  { name: "Queebi Gift Set", slug: "gift-set", image: "/images/products/gift-set.jpg" },
  { name: "Queebi Comforter", slug: "comforter", image: "/images/products/comforter.jpg" },
  { name: "Queebi PaciBuddy", slug: "pacibuddy", image: "/images/products/pacibuddy.jpg" },
  { name: "Queebi Swaddle", slug: "swaddle", image: "/images/products/swaddle.jpg" },
  { name: "Queebi Storage Box", slug: "storage-box", image: "/images/products/storage-box.jpg" },
  {
    name: "Queebi Nursery Wall Art Set",
    slug: "wall-decor",
    image: "/images/products/wall-decor.jpg",
    external: "https://www.amazon.com/Queebi-Kids-Nursery-Wall-Decor/dp/B0GT79PPQ6?ref_=ast_sto_dp",
    ctaKey: "viewOnAmazon",
  },
  {
    name: "Queebi LINE Stickers",
    slug: "line-stickers",
    image: "/images/products/line-stickers.jpg",
    external: "https://store.line.me/emojishop/product/67073f034694f37d422307f4/zh-Hant",
    ctaKey: "buyOnLine",
  },
];

const Products = () => {
  const { t } = useTranslation();
  const { prefix } = useLocale();
  const [gridRef, gridVisible] = useScrollAnimation<HTMLDivElement>();

  const productsGroups = (["sleep", "safety", "gifting"] as const)
    .map((k) => toFaqGroup(k, t(`faq.groups.${k}`, { returnObjects: true })))
    .filter(Boolean) as NonNullable<ReturnType<typeof toFaqGroup>>[];
  const productsList = itemListSchema({
    name: "Queebi Products",
    url: "https://queebi.com/queebi-products",
    items: products.map((p) => ({
      name: p.name,
      url: p.external ?? `https://queebi.com/queebi-products/${p.slug}`,
      image: p.image,
    })),
  });
  const collectionPage = collectionPageSchema({
    name: "Queebi Products",
    description: "Thoughtfully designed Queebi baby and toddler products: Gift Sets, Comforters, PaciBuddies, Swaddles, and Storage Boxes.",
    url: "https://queebi.com/queebi-products",
  });
  const breadcrumb = breadcrumbSchema([
    { name: "Home", url: "https://queebi.com/" },
    { name: "Products", url: "https://queebi.com/queebi-products" },
  ]);

  return (
    <>
      <PageHead
        title={t("products.seoTitle")}
        description={t("products.seoDesc")}
        canonical="https://queebi.com/queebi-products"
        image="/images/og/products-1200x630.jpg"
        imageAlt="Queebi baby & toddler products — PaciBuddies, comforters, gift sets and more"
        keywords="Queebi products, PaciBuddy, plush pacifier holder, pacifier with stuffed animal, paci buddy, paci plush, stuffed animal pacifier, detachable pacifier holder, wubbanub alternative, nookums alternative, nookums paci plushies alternative, comforter, baby gift set, organic baby swaddle, baby comforter blanket, newborn gift ideas, baby shower gift, gender neutral baby gift, baby registry must haves"
      />
      <JsonLd data={[productsList, collectionPage, breadcrumb]} />

      <section className="bg-background py-20 md:py-32">
        <div className="container max-w-3xl text-center mb-14">
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-8">{t("products.title")}</h1>
          <p className="text-muted-foreground text-lg mb-4">{t("products.desc1")}</p>
          <p className="text-muted-foreground text-lg">{t("products.desc2")}</p>
        </div>

        <div className="container mb-14">
          <img src="/images/products/hero.jpg" alt="Child with Queebi products" className="w-full max-w-3xl mx-auto rounded-2xl" width={900} height={560} loading="lazy" />
        </div>

        <div className="container text-center mb-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.amazon.com/stores/Kalianii-MotherlyLoveandLight/page/2E97BFF2-3CBA-47CB-8B3D-3D77AF27DFD0"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick('products_shop_amazon')}
            aria-label={t("products.shopOnAmazon")}
            className="inline-flex items-center justify-center w-full sm:w-auto sm:min-w-[280px] h-16 rounded-full bg-foreground text-background px-8 font-bold hover:bg-foreground/90 transition-all duration-200 hover:scale-105"
          >
            {t("products.shopOnAmazon")}
          </a>
          <a
            href="https://www.kalianii.com/pages/pacifier-holders"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick('usa_official_store')}
            aria-label="USA Official Store, use code BABY15 for 15% off your first order"
            className="inline-flex flex-col items-center justify-center w-full sm:w-auto sm:min-w-[280px] h-16 rounded-full bg-foreground text-background px-8 font-bold hover:bg-foreground/90 transition-all duration-200 hover:scale-105"
          >
            <span>USA Official Store</span>
            <span className="text-sm font-bold text-accent">Use code BABY15 → 15% off</span>
          </a>
        </div>

        <div className="container">
          <div
            ref={gridRef}
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-700 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {products.map((p) => {
              const cardInner = (
                <>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" width={500} height={375} loading="lazy" />
                  </div>
                  <div className="p-5 text-center">
                    <h2 className="font-bold text-lg">{p.name}</h2>
                    {p.ctaKey && (
                      <p className="mt-2 text-sm text-accent font-semibold">{t(`products.${p.ctaKey}`)} →</p>
                    )}
                  </div>
                </>
              );
              const cardClass =
                "group rounded-2xl overflow-hidden bg-card shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1";

              if (p.external) {
                return (
                  <a
                    key={p.slug}
                    href={p.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCTAClick(`product_${p.slug}`)}
                    className={cardClass}
                  >
                    {cardInner}
                  </a>
                );
              }
              return (
                <Link
                  key={p.slug}
                  to={`${prefix}/queebi-products/${p.slug}`}
                  onClick={() => trackCTAClick(`product_${p.slug}`)}
                  className={cardClass}
                >
                  {cardInner}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <FaqGroupsSection title={t("faq.title")} groups={productsGroups} />
    </>
  );
};

export default Products;
